import { execFile } from 'node:child_process';
import { createHash } from 'node:crypto';
import { open, readFile, readdir, stat } from 'node:fs/promises';
import { join, extname, basename, relative } from 'node:path';
import { promisify } from 'node:util';
import { minimatch as minimatchFn } from 'minimatch';
import { ChunkingService } from './chunking.service.js';
import { DriftService } from './drift.service.js';
import { createLogger } from '../logging/index.js';
import { createDocumentId } from '../types/brands.js';
import { ok, err } from '../types/result.js';
import { parseIgnorePatternsForScanning } from '../utils/ignore-patterns.js';
import type { CodeGraphService } from './code-graph.service.js';
import type { ManifestService } from './manifest.service.js';
import type { EmbeddingEngine } from '../db/embeddings.js';
import type { LanceStore } from '../db/lance.js';
import type { DocumentId } from '../types/brands.js';
import type { Document } from '../types/document.js';
import type { TypedStoreManifest, TypedFileState } from '../types/manifest.js';
import type { ProgressCallback } from '../types/progress.js';
import type { Result } from '../types/result.js';
import type { IngestConfig } from '../types/store-definition.js';
import type { Store, FileStore, RepoStore } from '../types/store.js';

// Type assertion for minimatch (version mismatch between package and @types)
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const minimatch = minimatchFn as (path: string, pattern: string) => boolean;

const execFileAsync = promisify(execFile);

const logger = createLogger('index-service');

export interface FilterStats {
  candidates: number;
  accepted: number;
  skippedExtension: number;
  skippedDirSegment: number;
  skippedIgnorePattern: number;
  skippedMinified: number;
  skippedExcluded: number;
  skippedLargeFiles: number;
  skippedLargeBytes: number;
  skippedBinary: number;
  skippedStatError: number;
}

interface IndexResult {
  filesIndexed: number;
  chunksCreated: number;
  timeMs: number;
  filterStats?: FilterStats;
}

interface IndexOptions {
  chunkSize?: number;
  chunkOverlap?: number;
  codeGraphService?: CodeGraphService;
  concurrency?: number;
  manifestService?: ManifestService;
  ignorePatterns?: readonly string[];
  prependPath?: boolean;
  maxFileSizeBytes?: number;
}

interface IncrementalIndexResult extends IndexResult {
  filesAdded: number;
  filesModified: number;
  filesDeleted: number;
  filesUnchanged: number;
}

const TEXT_EXTENSIONS = new Set([
  // Text/docs
  '.txt',
  '.md',
  '.mdx',
  '.rst',
  '.adoc',
  // JavaScript/TypeScript
  '.js',
  '.ts',
  '.jsx',
  '.tsx',
  '.mjs',
  '.cjs',
  '.mts',
  '.cts',
  // Config/data
  '.json',
  '.yaml',
  '.yml',
  '.toml',
  '.ini',
  '.env',
  // Web
  '.html',
  '.htm',
  '.css',
  '.scss',
  '.sass',
  '.less',
  '.vue',
  '.svelte',
  // Python
  '.py',
  '.pyi',
  '.pyx',
  // Ruby
  '.rb',
  '.erb',
  '.rake',
  // Go
  '.go',
  // Rust
  '.rs',
  // Java/JVM
  '.java',
  '.kt',
  '.kts',
  '.scala',
  '.groovy',
  '.gradle',
  // C/C++
  '.c',
  '.cpp',
  '.cc',
  '.cxx',
  '.h',
  '.hpp',
  '.hxx',
  // C#/.NET
  '.cs',
  '.fs',
  '.vb',
  // Swift/Objective-C
  '.swift',
  '.m',
  '.mm',
  // PHP
  '.php',
  // Shell
  '.sh',
  '.bash',
  '.zsh',
  '.fish',
  '.ps1',
  '.psm1',
  // SQL
  '.sql',
  // Other
  '.xml',
  '.graphql',
  '.gql',
  '.proto',
  '.lua',
  '.r',
  '.R',
  '.jl',
  '.ex',
  '.exs',
  '.erl',
  '.hrl',
  '.clj',
  '.cljs',
  '.cljc',
  '.hs',
  '.elm',
  '.dart',
  '.pl',
  '.pm',
  '.tcl',
  '.vim',
  '.zig',
  '.nim',
  '.v',
  '.tf',
  '.hcl',
  '.dockerfile',
  '.makefile',
  '.cmake',
]);

/**
 * Files matched by exact basename (no extension needed).
 * Covers important files that have no extension or non-standard extensions.
 */
const TEXT_BASENAMES = new Set([
  // Build systems
  'Dockerfile',
  'Makefile',
  'Rakefile',
  'Gemfile',
  'Procfile',
  'Vagrantfile',
  'Justfile',
  'Brewfile',
  'Earthfile',
  'Tiltfile',
  'CMakeLists.txt',
  'BUILD',
  'BUILD.bazel',
  'WORKSPACE',
  // Dotfiles (config)
  '.gitignore',
  '.gitattributes',
  '.editorconfig',
  '.dockerignore',
  '.eslintignore',
  '.prettierignore',
  '.npmignore',
  '.env.example',
  '.env.sample',
]);

/**
 * Check if a filename indicates a minified file.
 * Matches: *.min.js, *.min.css, *.bundle.js, etc.
 */
function isMinifiedFile(filename: string): boolean {
  const minPatterns = [
    /\.min\.(js|css|mjs|cjs)$/i,
    /\.bundle\.(js|mjs|cjs)$/i,
    /\.prod\.(js|mjs|cjs)$/i,
  ];
  return minPatterns.some((p) => p.test(filename));
}

/**
 * Detect if file content appears to be binary.
 * Uses null-byte heuristic on first 8KB.
 */
async function isBinaryFile(filePath: string): Promise<boolean> {
  const SAMPLE_SIZE = 8192;
  const fd = await open(filePath, 'r');
  try {
    const buffer = Buffer.alloc(SAMPLE_SIZE);
    const { bytesRead } = await fd.read(buffer, 0, SAMPLE_SIZE, 0);
    // Check for null bytes (common in binary files)
    for (let i = 0; i < bytesRead; i++) {
      if (buffer[i] === 0) return true;
    }
    return false;
  } finally {
    await fd.close();
  }
}

/** Map file extensions to language identifiers for metadata enrichment. */
const EXT_TO_LANGUAGE: Record<string, string> = {
  '.ts': 'typescript',
  '.tsx': 'typescript',
  '.mts': 'typescript',
  '.cts': 'typescript',
  '.js': 'javascript',
  '.jsx': 'javascript',
  '.mjs': 'javascript',
  '.cjs': 'javascript',
  '.py': 'python',
  '.pyi': 'python',
  '.pyx': 'python',
  '.rs': 'rust',
  '.go': 'go',
  '.java': 'java',
  '.kt': 'kotlin',
  '.kts': 'kotlin',
  '.scala': 'scala',
  '.groovy': 'groovy',
  '.rb': 'ruby',
  '.erb': 'ruby',
  '.rake': 'ruby',
  '.php': 'php',
  '.swift': 'swift',
  '.m': 'objective-c',
  '.mm': 'objective-c',
  '.c': 'c',
  '.h': 'c',
  '.cpp': 'cpp',
  '.cc': 'cpp',
  '.cxx': 'cpp',
  '.hpp': 'cpp',
  '.hxx': 'cpp',
  '.cs': 'csharp',
  '.fs': 'fsharp',
  '.vb': 'vb',
  '.sh': 'shell',
  '.bash': 'shell',
  '.zsh': 'shell',
  '.fish': 'shell',
  '.ps1': 'powershell',
  '.psm1': 'powershell',
  '.sql': 'sql',
  '.md': 'markdown',
  '.mdx': 'mdx',
  '.rst': 'restructuredtext',
  '.lua': 'lua',
  '.r': 'r',
  '.R': 'r',
  '.jl': 'julia',
  '.ex': 'elixir',
  '.exs': 'elixir',
  '.erl': 'erlang',
  '.hrl': 'erlang',
  '.clj': 'clojure',
  '.cljs': 'clojure',
  '.cljc': 'clojure',
  '.hs': 'haskell',
  '.elm': 'elm',
  '.dart': 'dart',
  '.zig': 'zig',
  '.nim': 'nim',
  '.v': 'v',
  '.pl': 'perl',
  '.pm': 'perl',
  '.tf': 'terraform',
  '.hcl': 'hcl',
  '.proto': 'protobuf',
  '.graphql': 'graphql',
  '.gql': 'graphql',
  '.vue': 'vue',
  '.svelte': 'svelte',
  '.html': 'html',
  '.htm': 'html',
  '.css': 'css',
  '.scss': 'scss',
  '.sass': 'sass',
  '.less': 'less',
  '.json': 'json',
  '.yaml': 'yaml',
  '.yml': 'yaml',
  '.toml': 'toml',
  '.xml': 'xml',
};

/** Language lookup for files matched by basename (no extension). */
const BASENAME_TO_LANGUAGE: Record<string, string> = {
  Dockerfile: 'dockerfile',
  Makefile: 'makefile',
  Rakefile: 'ruby',
  Gemfile: 'ruby',
  Brewfile: 'ruby',
  'CMakeLists.txt': 'cmake',
  BUILD: 'starlark',
  'BUILD.bazel': 'starlark',
  WORKSPACE: 'starlark',
  Tiltfile: 'starlark',
  Justfile: 'just',
};

/** Filenames that typically represent entry points / public API surfaces. */
const ENTRY_POINT_NAMES = new Set([
  'index.ts',
  'index.js',
  'index.mjs',
  'index.tsx',
  'index.jsx',
  'main.ts',
  'main.js',
  'main.py',
  'main.go',
  'main.rs',
  'app.ts',
  'app.js',
  'app.py',
  'mod.rs',
  'lib.rs',
  '__init__.py',
  'server.ts',
  'server.js',
  'server.py',
]);

export class IndexService {
  private readonly lanceStore: LanceStore;
  private readonly embeddingEngine: EmbeddingEngine;
  private readonly chunker: ChunkingService;
  private readonly codeGraphService: CodeGraphService | undefined;
  private readonly manifestService: ManifestService | undefined;
  private readonly driftService: DriftService;
  private readonly concurrency: number;
  private readonly ignoreDirs: Set<string>;
  private readonly ignoreFilePatterns: Array<(filename: string) => boolean>;
  private readonly prependPath: boolean;
  private readonly maxFileSizeBytes: number;

  constructor(
    lanceStore: LanceStore,
    embeddingEngine: EmbeddingEngine,
    options: IndexOptions = {}
  ) {
    this.lanceStore = lanceStore;
    this.embeddingEngine = embeddingEngine;
    this.chunker = new ChunkingService({
      chunkSize: options.chunkSize ?? 1000,
      chunkOverlap: options.chunkOverlap ?? 150,
    });
    this.codeGraphService = options.codeGraphService;
    this.manifestService = options.manifestService;
    this.driftService = new DriftService();
    this.concurrency = options.concurrency ?? 4;
    this.prependPath = options.prependPath ?? false;

    const parsed = parseIgnorePatternsForScanning(options.ignorePatterns ?? []);
    this.ignoreDirs = parsed.dirs;
    this.ignoreFilePatterns = parsed.fileMatchers;
    this.maxFileSizeBytes = options.maxFileSizeBytes ?? 1_048_576; // 1MB default
  }

  async indexStore(store: Store, onProgress?: ProgressCallback): Promise<Result<IndexResult>> {
    logger.info(
      {
        storeId: store.id,
        storeName: store.name,
        storeType: store.type,
      },
      'Starting store indexing'
    );

    try {
      if (store.type === 'file' || store.type === 'repo') {
        return await this.indexFileStore(store, onProgress);
      }

      logger.error(
        { storeId: store.id, storeType: store.type },
        'Unsupported store type for indexing'
      );
      return err(new Error(`Indexing not supported for store type: ${store.type}`));
    } catch (error) {
      logger.error(
        {
          storeId: store.id,
          error: error instanceof Error ? error.message : String(error),
        },
        'Store indexing failed'
      );
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Incrementally index a store, only processing changed files.
   * Requires manifestService to be configured.
   *
   * @param store - The store to index
   * @param onProgress - Optional progress callback
   * @returns Result with incremental index statistics
   */
  async indexStoreIncremental(
    store: Store,
    onProgress?: ProgressCallback
  ): Promise<Result<IncrementalIndexResult>> {
    if (this.manifestService === undefined) {
      return err(new Error('ManifestService required for incremental indexing'));
    }

    if (store.type !== 'file' && store.type !== 'repo') {
      return err(new Error(`Incremental indexing not supported for store type: ${store.type}`));
    }

    logger.info(
      {
        storeId: store.id,
        storeName: store.name,
        storeType: store.type,
      },
      'Starting incremental store indexing'
    );

    const startTime = Date.now();

    try {
      // Load manifest
      const manifest = await this.manifestService.load(store.id);

      // Discover files (git ls-files for git repos, filesystem walk otherwise)
      const filePaths = await this.discoverFiles(store.path, store.ingest);
      const currentFiles = await Promise.all(
        filePaths.map((path) => this.driftService.getFileState(path))
      );

      // Detect changes
      const drift = await this.driftService.detectChanges(manifest, currentFiles);

      logger.debug(
        {
          storeId: store.id,
          added: drift.added.length,
          modified: drift.modified.length,
          deleted: drift.deleted.length,
          unchanged: drift.unchanged.length,
        },
        'Drift detection complete'
      );

      // Collect document IDs to delete (from modified and deleted files)
      const documentIdsToDelete: DocumentId[] = [];
      for (const path of [...drift.modified, ...drift.deleted]) {
        const fileState = manifest.files[path];
        if (fileState !== undefined) {
          documentIdsToDelete.push(...fileState.documentIds);
        }
      }

      // Delete old documents
      if (documentIdsToDelete.length > 0) {
        await this.lanceStore.deleteDocuments(store.id, documentIdsToDelete);
        logger.debug(
          { storeId: store.id, count: documentIdsToDelete.length },
          'Deleted old documents'
        );
      }

      // Process new and modified files
      const filesToProcess = [...drift.added, ...drift.modified];
      const totalFiles = filesToProcess.length;

      onProgress?.({
        type: 'start',
        current: 0,
        total: totalFiles,
        message: `Processing ${String(totalFiles)} changed files`,
      });

      const documents: Document[] = [];
      const newManifestFiles: Record<string, TypedFileState> = {};
      let filesProcessed = 0;

      // Keep unchanged files in manifest
      for (const path of drift.unchanged) {
        const existingState = manifest.files[path];
        if (existingState !== undefined) {
          newManifestFiles[path] = existingState;
        }
      }

      // Process changed files in parallel batches
      for (let i = 0; i < filesToProcess.length; i += this.concurrency) {
        const batch = filesToProcess.slice(i, i + this.concurrency);

        const batchResults = await Promise.all(
          batch.map(async (filePath) => {
            try {
              const result = await this.processFile(filePath, store);
              const documentIds = result.documents.map((d) => d.id);

              // Create file state for manifest
              const { state } = await this.driftService.createFileState(filePath, documentIds);

              return {
                filePath,
                documents: result.documents,
                fileState: state,
              };
            } catch (error) {
              logger.warn(
                { filePath, error: error instanceof Error ? error.message : String(error) },
                'Failed to process file during incremental indexing, skipping'
              );
              return null;
            }
          })
        );

        // Collect results (skip null entries from failed files)
        for (const result of batchResults) {
          if (result !== null) {
            documents.push(...result.documents);
            newManifestFiles[result.filePath] = result.fileState;
          }
        }

        filesProcessed += batch.length;

        onProgress?.({
          type: 'progress',
          current: filesProcessed,
          total: totalFiles,
          message: `Processed ${String(filesProcessed)}/${String(totalFiles)} files`,
        });
      }

      // Add new documents
      if (documents.length > 0) {
        await this.lanceStore.addDocuments(store.id, documents);
      }

      // Recreate FTS index if any changes occurred (deletions or additions)
      if (documentIdsToDelete.length > 0 || documents.length > 0) {
        await this.lanceStore.createFtsIndex(store.id);
      }

      // Rebuild code graph if service available and source files changed
      if (this.codeGraphService) {
        const sourceExtensions = ['.ts', '.tsx', '.js', '.jsx', '.py', '.rs', '.go'];
        const hasSourceChanges =
          filesToProcess.some((p) => sourceExtensions.includes(extname(p).toLowerCase())) ||
          drift.deleted.some((p) => sourceExtensions.includes(extname(p).toLowerCase()));

        if (hasSourceChanges) {
          // Rebuild full graph from all current source files (simpler than incremental updates)
          const allSourceFiles: Array<{ path: string; content: string }> = [];
          const allPaths = [...drift.unchanged, ...filesToProcess];

          for (const filePath of allPaths) {
            const ext = extname(filePath).toLowerCase();
            if (sourceExtensions.includes(ext)) {
              try {
                const content = await readFile(filePath, 'utf-8');
                allSourceFiles.push({ path: filePath, content });
              } catch {
                // File may have been deleted between scan and read
              }
            }
          }

          if (allSourceFiles.length > 0) {
            const graph = await this.codeGraphService.buildGraph(allSourceFiles);
            await this.codeGraphService.saveGraph(store.id, graph);
            logger.debug(
              { storeId: store.id, sourceFiles: allSourceFiles.length },
              'Rebuilt code graph during incremental indexing'
            );
          } else {
            // No source files remain - delete stale graph
            await this.codeGraphService.deleteGraph(store.id);
            logger.debug(
              { storeId: store.id },
              'Deleted stale code graph (no source files remain)'
            );
          }
        }
      }

      // Save updated manifest
      const updatedManifest: TypedStoreManifest = {
        version: 1,
        storeId: store.id,
        indexedAt: new Date().toISOString(),
        files: newManifestFiles,
      };
      await this.manifestService.save(updatedManifest);

      onProgress?.({
        type: 'complete',
        current: totalFiles,
        total: totalFiles,
        message: 'Incremental indexing complete',
      });

      const timeMs = Date.now() - startTime;

      logger.info(
        {
          storeId: store.id,
          storeName: store.name,
          filesAdded: drift.added.length,
          filesModified: drift.modified.length,
          filesDeleted: drift.deleted.length,
          filesUnchanged: drift.unchanged.length,
          chunksCreated: documents.length,
          timeMs,
        },
        'Incremental indexing complete'
      );

      return ok({
        filesIndexed: filesToProcess.length,
        chunksCreated: documents.length,
        timeMs,
        filesAdded: drift.added.length,
        filesModified: drift.modified.length,
        filesDeleted: drift.deleted.length,
        filesUnchanged: drift.unchanged.length,
      });
    } catch (error) {
      logger.error(
        {
          storeId: store.id,
          error: error instanceof Error ? error.message : String(error),
        },
        'Incremental indexing failed'
      );
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private async indexFileStore(
    store: FileStore | RepoStore,
    onProgress?: ProgressCallback
  ): Promise<Result<IndexResult>> {
    const startTime = Date.now();

    // Clear existing documents before full re-index to prevent duplicates
    await this.lanceStore.clearAllDocuments(store.id);

    // Clear stale manifest to ensure fresh incremental indexing later
    if (this.manifestService) {
      await this.manifestService.delete(store.id);
    }

    const files = await this.discoverFiles(store.path, store.ingest);
    const documents: Document[] = [];
    let filesProcessed = 0;

    logger.debug(
      {
        storeId: store.id,
        path: store.path,
        fileCount: files.length,
        concurrency: this.concurrency,
        ingestConfig: store.ingest,
      },
      'Files scanned for indexing'
    );

    // Collect source files for code graph building
    const sourceFiles: Array<{ path: string; content: string }> = [];

    // Emit start event
    onProgress?.({
      type: 'start',
      current: 0,
      total: files.length,
      message: 'Starting index',
    });

    // Process files in parallel batches
    for (let i = 0; i < files.length; i += this.concurrency) {
      const batch = files.slice(i, i + this.concurrency);

      const batchResults = await Promise.all(
        batch.map(async (filePath) => {
          try {
            return await this.processFile(filePath, store);
          } catch (error) {
            logger.warn(
              { filePath, error: error instanceof Error ? error.message : String(error) },
              'Failed to process file, skipping'
            );
            return { documents: [], sourceFile: undefined };
          }
        })
      );

      // Collect results from batch
      for (const result of batchResults) {
        documents.push(...result.documents);
        if (result.sourceFile !== undefined) {
          sourceFiles.push(result.sourceFile);
        }
      }

      filesProcessed += batch.length;

      // Emit progress event after each batch with enhanced details
      const elapsedMs = Date.now() - startTime;
      const filesPerSecond = elapsedMs > 0 ? (filesProcessed / elapsedMs) * 1000 : 0;
      const lastFile = batch.at(-1);

      onProgress?.({
        type: 'progress',
        current: filesProcessed,
        total: files.length,
        message: `Indexed ${String(filesProcessed)}/${String(files.length)} files`,
        details: {
          ...(lastFile !== undefined ? { currentFile: basename(lastFile) } : {}),
          elapsedMs,
          filesPerSecond,
        },
      });
    }

    if (documents.length > 0) {
      await this.lanceStore.addDocuments(store.id, documents);
      // Create FTS index for full-text search
      await this.lanceStore.createFtsIndex(store.id);
    }

    // Build and save code graph if service is available and we have source files
    if (this.codeGraphService && sourceFiles.length > 0) {
      const graph = await this.codeGraphService.buildGraph(sourceFiles);
      await this.codeGraphService.saveGraph(store.id, graph);
    } else if (this.codeGraphService) {
      // No source files - delete any stale graph
      await this.codeGraphService.deleteGraph(store.id);
    }

    const timeMs = Date.now() - startTime;

    // Emit complete event with final timing
    onProgress?.({
      type: 'complete',
      current: files.length,
      total: files.length,
      message: 'Indexing complete',
      details: {
        elapsedMs: timeMs,
        filesPerSecond: timeMs > 0 ? (files.length / timeMs) * 1000 : 0,
      },
    });

    logger.info(
      {
        storeId: store.id,
        storeName: store.name,
        filesIndexed: filesProcessed,
        chunksCreated: documents.length,
        sourceFilesForGraph: sourceFiles.length,
        timeMs,
      },
      'Store indexing complete'
    );

    return ok({
      filesIndexed: filesProcessed,
      chunksCreated: documents.length,
      timeMs,
    });
  }

  /**
   * Process a single file: read, chunk, embed, and return documents.
   * Extracted for parallel processing.
   */
  private async processFile(
    filePath: string,
    store: FileStore | RepoStore
  ): Promise<{
    documents: Document[];
    sourceFile: { path: string; content: string } | undefined;
  }> {
    const rawContent = await readFile(filePath, 'utf-8');
    const fileHash = createHash('md5').update(rawContent).digest('hex');

    // Use relative path for document ID and optional context enrichment
    const relativePath = relative(store.path, filePath);

    // Optionally prepend file path for context enrichment (Phase 3 optimization)
    const content = this.prependPath ? `[${relativePath}]\n${rawContent}` : rawContent;

    const chunks = this.chunker.chunk(content, filePath);
    const pathHash = createHash('md5').update(relativePath).digest('hex').slice(0, 8);

    const ext = extname(filePath).toLowerCase();
    const fileName = basename(filePath).toLowerCase();
    const fileType = this.classifyFileType(ext, fileName, filePath);

    // Metadata enrichment: language, depth, entry-point detection
    const language = EXT_TO_LANGUAGE[ext] ?? BASENAME_TO_LANGUAGE[basename(filePath)];
    const normalizedRelPath = relativePath.replaceAll('\\', '/');
    const depth = normalizedRelPath.split('/').length - 1;
    const isEntryPoint = ENTRY_POINT_NAMES.has(basename(filePath));

    // Track source file for code graph (supports JS/TS, Python, Rust, Go)
    const sourceFile = ['.ts', '.tsx', '.js', '.jsx', '.py', '.rs', '.go'].includes(ext)
      ? { path: filePath, content }
      : undefined;

    // Skip files with no chunks (empty files)
    if (chunks.length === 0) {
      return { documents: [], sourceFile };
    }

    // Batch embed all chunks from this file
    const chunkContents = chunks.map((c) => c.content);
    const vectors = await this.embeddingEngine.embedBatch(chunkContents);

    const documents: Document[] = [];
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const vector = vectors[i];

      // Fail fast if chunk/vector mismatch (should never happen)
      if (chunk === undefined || vector === undefined) {
        throw new Error(
          `Chunk/vector mismatch at index ${String(i)}: chunk=${String(chunk !== undefined)}, vector=${String(vector !== undefined)}`
        );
      }

      // Include pathHash in ID to prevent collisions when files have identical content
      const chunkId =
        chunks.length > 1
          ? `${store.id}-${pathHash}-${fileHash}-${String(chunk.chunkIndex)}`
          : `${store.id}-${pathHash}-${fileHash}`;

      documents.push({
        id: createDocumentId(chunkId),
        content: chunk.content,
        vector,
        metadata: {
          type: chunks.length > 1 ? 'chunk' : 'file',
          storeId: store.id,
          path: filePath,
          indexedAt: new Date().toISOString(),
          fileHash,
          chunkIndex: chunk.chunkIndex,
          totalChunks: chunk.totalChunks,
          fileType,
          sectionHeader: chunk.sectionHeader,
          functionName: chunk.functionName,
          hasDocComments: /\/\*\*[\s\S]*?\*\//.test(chunk.content),
          docSummary: chunk.docSummary,
          relativePath: normalizedRelPath,
          language,
          depth,
          isEntryPoint,
        },
      });
    }

    return { documents, sourceFile };
  }

  /**
   * Get tracked files from git. Returns null if not a git repo or on error.
   */
  private async getTrackedFiles(repoPath: string): Promise<string[] | null> {
    try {
      // Check if .git exists first
      const gitDir = join(repoPath, '.git');
      try {
        await stat(gitDir);
      } catch {
        logger.info({ repoPath }, 'Not a git repository, using filesystem walk');
        return null;
      }

      const { stdout } = await execFileAsync('git', ['ls-files', '-z'], {
        cwd: repoPath,
        maxBuffer: 100 * 1024 * 1024, // 100MB for large monorepos
      });

      const files = stdout.split('\0').filter(Boolean);

      // Log submodule detection (if any gitmodules tracked)
      if (files.some((f) => f === '.gitmodules')) {
        logger.info({ repoPath }, 'Repository has submodules (skipped by ls-files)');
      }

      return files;
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      logger.warn({ repoPath, reason }, 'git ls-files failed, using filesystem walk');
      return null;
    }
  }

  /**
   * Discover files for indexing. Uses git ls-files for git repos, filesystem walk otherwise.
   * Always applies extension filter, ignore patterns, and size limit.
   * Optionally applies per-store ingest configuration.
   */
  private async discoverFiles(storePath: string, ingestConfig?: IngestConfig): Promise<string[]> {
    const trackedFiles = await this.getTrackedFiles(storePath);

    let candidates: string[];
    if (trackedFiles !== null) {
      // Convert relative paths to absolute
      candidates = trackedFiles.map((f) => join(storePath, f));
      logger.debug({ storePath, count: candidates.length }, 'Using git ls-files for discovery');
    } else {
      // Non-git directory: use filesystem walk
      candidates = await this.scanDirectory(storePath);
      logger.debug({ storePath, count: candidates.length }, 'Using filesystem walk for discovery');
    }

    // Apply filters to BOTH paths (consistency)
    const { files } = await this.filterFiles(candidates, storePath, ingestConfig);
    return files;
  }

  /**
   * Apply extension filter, ignore patterns, size limit, and per-store ingest filters.
   * Reuses existing ignoreFilePatterns from parseIgnorePatternsForScanning.
   */
  private async filterFiles(
    files: string[],
    storePath: string,
    ingestConfig?: IngestConfig
  ): Promise<{ files: string[]; stats: FilterStats }> {
    const result: string[] = [];
    const skippedLarge: Array<{ path: string; size: number }> = [];
    const skippedMinified: string[] = [];
    const skippedBinary: string[] = [];
    const skippedExcluded: string[] = [];
    let skippedExtension = 0;
    let skippedDirSegment = 0;
    let skippedIgnorePattern = 0;
    let skippedStatError = 0;

    // Per-store overrides
    const maxFileSize = ingestConfig?.maxFileSizeBytes ?? this.maxFileSizeBytes;
    const skipMinifiedOpt = ingestConfig?.skipMinified ?? true;
    const skipBinaries = ingestConfig?.skipBinaries ?? true;
    const excludeGlobs: string[] = ingestConfig?.excludeGlobs ?? [];
    const maxFiles = ingestConfig?.maxFiles;

    for (const filePath of files) {
      const ext = extname(filePath).toLowerCase();
      const filename = basename(filePath);

      // Extension/basename allowlist — same as scanDirectory
      if (!TEXT_EXTENSIONS.has(ext) && !TEXT_BASENAMES.has(filename)) {
        skippedExtension++;
        continue;
      }

      // Path-segment directory filtering (critical for git ls-files paths)
      // git ls-files returns relative paths like 'vendor/lib/utils.js' — we must
      // check if any directory segment matches an ignored directory name.
      const relativePath = relative(storePath, filePath).replaceAll('\\', '/');
      const pathSegments = relativePath.split('/');
      const dirSegments = pathSegments.slice(0, -1);
      if (dirSegments.some((segment) => this.ignoreDirs.has(segment))) {
        skippedDirSegment++;
        continue;
      }

      // Ignore patterns - reuse existing this.ignoreFilePatterns
      const shouldIgnore = this.ignoreFilePatterns.some((matcher) => matcher(filename));
      if (shouldIgnore) {
        skippedIgnorePattern++;
        continue;
      }

      // Minified file filter (default: skip)
      if (skipMinifiedOpt && isMinifiedFile(filename)) {
        skippedMinified.push(filename);
        continue;
      }

      // Per-store excludeGlobs
      if (excludeGlobs.length > 0) {
        const relativePath = relative(storePath, filePath);
        const excluded = excludeGlobs.some((glob: string) => minimatch(relativePath, glob));
        if (excluded) {
          skippedExcluded.push(relativePath);
          continue;
        }
      }

      // Size limit (stat before read)
      let fileSize: number;
      try {
        const stats = await stat(filePath);
        fileSize = stats.size;
        if (fileSize > maxFileSize) {
          skippedLarge.push({ path: filePath, size: fileSize });
          continue;
        }
      } catch {
        // File may have been deleted between discovery and stat
        skippedStatError++;
        continue;
      }

      // Binary file filter (default: skip)
      if (skipBinaries) {
        try {
          if (await isBinaryFile(filePath)) {
            skippedBinary.push(filename);
            continue;
          }
        } catch {
          // If we can't read, skip
          skippedStatError++;
          continue;
        }
      }

      result.push(filePath);
    }

    // Apply maxFiles cap (after all other filters)
    if (maxFiles !== undefined && result.length > maxFiles) {
      logger.info({ storePath, total: result.length, maxFiles }, 'Applying maxFiles cap');
      result.length = maxFiles;
    }

    const skippedLargeBytes = skippedLarge.reduce((sum, f) => sum + f.size, 0);

    // Log skipped files
    if (skippedLarge.length > 0) {
      logger.info(
        {
          storePath,
          count: skippedLarge.length,
          totalBytes: skippedLargeBytes,
          examples: skippedLarge.slice(0, 5).map((f) => relative(storePath, f.path)),
        },
        'Skipped large files'
      );
    }
    if (skippedMinified.length > 0) {
      logger.debug(
        { storePath, count: skippedMinified.length, examples: skippedMinified.slice(0, 5) },
        'Skipped minified files'
      );
    }
    if (skippedBinary.length > 0) {
      logger.debug(
        { storePath, count: skippedBinary.length, examples: skippedBinary.slice(0, 5) },
        'Skipped binary files'
      );
    }
    if (skippedExcluded.length > 0) {
      logger.debug(
        { storePath, count: skippedExcluded.length, examples: skippedExcluded.slice(0, 5) },
        'Skipped excluded files'
      );
    }

    const stats: FilterStats = {
      candidates: files.length,
      accepted: result.length,
      skippedExtension,
      skippedDirSegment,
      skippedIgnorePattern,
      skippedMinified: skippedMinified.length,
      skippedExcluded: skippedExcluded.length,
      skippedLargeFiles: skippedLarge.length,
      skippedLargeBytes,
      skippedBinary: skippedBinary.length,
      skippedStatError,
    };

    logger.info({ storePath, ...stats }, 'File filtering complete');

    return { files: result, stats };
  }

  private async scanDirectory(dir: string): Promise<string[]> {
    const files: string[] = [];
    const entries = await readdir(dir, { withFileTypes: true });

    for (const entry of entries) {
      const fullPath = join(dir, entry.name);

      if (entry.isDirectory()) {
        // Skip directories matching ignore patterns
        if (!this.ignoreDirs.has(entry.name)) {
          files.push(...(await this.scanDirectory(fullPath)));
        }
      } else if (entry.isFile()) {
        // Skip files matching ignore patterns (e.g., *.min.js, *.map)
        const shouldIgnore = this.ignoreFilePatterns.some((matcher) => matcher(entry.name));
        if (shouldIgnore) {
          continue;
        }

        const ext = extname(entry.name).toLowerCase();
        if (TEXT_EXTENSIONS.has(ext) || TEXT_BASENAMES.has(entry.name)) {
          files.push(fullPath);
        }
      }
    }

    return files;
  }

  /**
   * Classify file type for ranking purposes.
   * Documentation files rank higher than source code for documentation queries.
   * Phase 4: Enhanced to detect internal implementation files.
   */
  private classifyFileType(ext: string, fileName: string, filePath: string): string {
    // Documentation files
    if (ext === '.md') {
      // CHANGELOG files get their own category for intent-based penalties
      if (fileName === 'changelog.md' || fileName === 'changes.md' || /changelog/i.test(fileName)) {
        return 'changelog';
      }
      // Special doc files get highest priority
      if (['readme.md', 'migration.md', 'contributing.md'].includes(fileName)) {
        return 'documentation-primary';
      }
      // Check path for documentation indicators
      if (/\/(docs?|documentation|guides?|tutorials?|articles?)\//i.test(filePath)) {
        return 'documentation';
      }
      return 'documentation';
    }

    // Test files
    if (/\.(test|spec)\.[jt]sx?$/.test(fileName) || /\/__tests__\//.test(filePath)) {
      return 'test';
    }

    // Example files
    if (/\/examples?\//.test(filePath) || fileName.includes('example')) {
      return 'example';
    }

    // Config files
    if (/^(tsconfig|package|\.eslint|\.prettier|vite\.config|next\.config)/i.test(fileName)) {
      return 'config';
    }

    // Source code - distinguish between internal and public-facing
    if (['.ts', '.tsx', '.js', '.jsx', '.py', '.go', '.rs', '.java'].includes(ext)) {
      // Internal implementation files (monorepo packages, lib internals)
      // These patterns indicate internal/core implementation code
      if (this.isInternalImplementation(filePath, fileName)) {
        return 'source-internal';
      }
      return 'source';
    }

    return 'other';
  }

  /**
   * Detect if a source file is internal implementation code.
   * Internal code should rank lower than public-facing APIs and docs.
   */
  private isInternalImplementation(filePath: string, fileName: string): boolean {
    const pathLower = filePath.toLowerCase();
    const fileNameLower = fileName.toLowerCase();

    // Monorepo internal packages (like Vue's packages/*/src/)
    if (/\/packages\/[^/]+\/src\//.test(pathLower)) {
      // Exception: index files often export public APIs
      if (fileNameLower === 'index.ts' || fileNameLower === 'index.js') {
        return false;
      }
      return true;
    }

    // Internal/core directories
    if (/\/(internal|lib\/core|core\/src|_internal|private)\//.test(pathLower)) {
      return true;
    }

    // Compiler/transform internals (often not what users want)
    if (
      /\/(compiler|transforms?|parse|codegen)\//.test(pathLower) &&
      !fileNameLower.includes('readme') &&
      !fileNameLower.includes('index')
    ) {
      return true;
    }

    return false;
  }
}

/**
 * Classify web content type based on URL patterns and page title.
 * Used for ranking boosts similar to local file classification.
 */
export function classifyWebContentType(url: string, title?: string): string {
  const urlLower = url.toLowerCase();
  const titleLower = (title ?? '').toLowerCase();

  // API reference documentation → documentation-primary (1.8x boost)
  if (
    /\/api[-/]?(ref|reference|docs?)?\//i.test(urlLower) ||
    /api\s*(reference|documentation)/i.test(titleLower)
  ) {
    return 'documentation-primary';
  }

  // Getting started / tutorials → documentation-primary (1.8x boost)
  if (
    /\/(getting[-_]?started|quickstart|tutorial|setup)\b/i.test(urlLower) ||
    /(getting started|quickstart|tutorial)/i.test(titleLower)
  ) {
    return 'documentation-primary';
  }

  // General docs paths → documentation (1.5x boost)
  if (/\/(docs?|documentation|reference|learn|manual|guide)/i.test(urlLower)) {
    return 'documentation';
  }

  // Examples and demos → example (1.4x boost)
  if (/\/(examples?|demos?|samples?|cookbook)/i.test(urlLower)) {
    return 'example';
  }

  // Changelog → changelog (special handling in intent boosts)
  if (/changelog|release[-_]?notes/i.test(urlLower)) {
    return 'changelog';
  }

  // Blog posts → lower priority
  if (/\/blog\//i.test(urlLower)) {
    return 'other';
  }

  // Web content without specific path indicators is treated as documentation
  return 'documentation';
}
